import { request } from '@api/request'
import { DownloadHelper } from '@helpers/DownloadHelper'
import { AvailableLanguage } from '@helpers/language/AvailableLanguages'
import { Language } from '@models/Language'
import { Level } from '@models/Level'
import { Model } from '@models/Model'
import { BulletinDTO } from '@models/api/bulletin/BulletinDTO'
import { BulletinInfoDTO } from '@models/api/bulletin/BulletinInfoDTO'
import { ModelDatesDTO } from '@models/api/bulletin/ModelDatesDTO'
import { RequestBulletinMailConfigurationDTO } from '@models/api/bulletin/RequestBulletinMailConfigurationDTO'
import { RelativeDateDTO } from '@models/api/time/RelativeDateDTO'
import { Bulletin } from '@models/bulletin/Bulletin'
import { Position } from '@models/map/Position'
import { showToastError } from '@services/ToastService'
import { AxiosPromise, HttpStatusCode } from 'axios'
import { Dayjs } from 'dayjs'
import { newI18n } from 'src/i18n'

const { t } = newI18n.global
const BASE_URL = 'bulletinConfiguration'

function getBulletinPDFUrl(
  bulletinID: Bulletin['id'],
  position?: Position,
  donwloadID?: string
) {
  let url = `${BASE_URL}/generateBulletinPDF/${bulletinID}`

  if (position) {
    url += `?lat=${position.lat}&lon=${position.lon}`
  }
  if (donwloadID) {
    url += `?uuid=${donwloadID}`
  }
  return url
}

export const bulletinConfiguration = {
  get: function (bulletinID: string): AxiosPromise<BulletinInfoDTO> {
    return request.get(`${BASE_URL}/${bulletinID}`)
  },

  getAll: function (): AxiosPromise<BulletinInfoDTO[]> {
    return request.get(BASE_URL)
  },

  create: function (bulletin: BulletinDTO) {
    return request.post(BASE_URL, {
      TITLE: bulletin.name,
      ID_BULLETIN: bulletin.id,
      JSON: bulletin
    } as BulletinInfoDTO)
  },

  edit: function (bulletin: BulletinDTO) {
    return request.put(`${BASE_URL}/${bulletin.id}`, {
      TITLE: bulletin.name,
      ID_BULLETIN: bulletin.id,
      JSON: bulletin
    } as BulletinInfoDTO)
  },

  delete: async function (bulletinID: string) {
    return await request.delete(`${BASE_URL}/${bulletinID}`)
  },

  getLevelAvailableDates: async (
    levelID: Level['id']
  ): Promise<ModelDatesDTO> => {
    const url = `${BASE_URL}/fgb/getLevelAvailableDates`
    return request
      .get(url, {
        params: { levelId: levelID }
      })
      .then((response) => {
        return response.data
      })
  },

  getBulletinPDFUrl: (bulletinID: Bulletin['id'], position?: Position) => {
    return getBulletinPDFUrl(bulletinID, position)
  },

  downloadMapImage: async (
    bulletinID: Bulletin['id'],
    levelID: Level['id'],
    mapDate: Dayjs,
    isWindActive: boolean = false
  ) => {
    const url = `${BASE_URL}/map/getImage`
    return request
      .get(url, {
        params: {
          bulletinID,
          levelID,
          mapDate: mapDate.toISOString(),
          isWindActive
        },
        responseType: 'blob'
      })
      .then((response) => {
        return response.data
      })
  },

  downloadLegendImage: async (
    levelID: Level['id'],
    language: Language['code'] = AvailableLanguage.ES
  ) => {
    const url = `${BASE_URL}/map/getLegend`
    return request
      .get(url, {
        params: { levelID, language },
        responseType: 'blob'
      })
      .then((response) => {
        return response.data
      })
  },

  downloadBulletinPDF: async (
    bulletinID: Bulletin['id'],
    downloadID?: string
  ) => {
    const url = getBulletinPDFUrl(bulletinID, undefined, downloadID)
    return request.get<File | BlobPart>(url, {
      responseType: 'blob'
    })
  },

  sendBulletinPDF: async (bulletinID: Bulletin['id'], emails: string[]) => {
    const url = `${BASE_URL}/sendMail/${bulletinID}`
    return await request.post(url, { to: emails })
  },

  downloadChartImage: async (
    bulletinID: Bulletin['id'],
    levelID: Level['id'],
    relativeDate: RelativeDateDTO
  ) => {
    const url = `${BASE_URL}/chart/getChart`
    return request
      .post(
        url,
        { bulletinID, levelID, relativeDate },
        {
          responseType: 'blob'
        }
      )
      .then((response) => {
        return response.data
      })
  },

  downloadTephigramImage: async (
    bulletinID: Bulletin['id'],
    modelID: Model['id'],
    date: Dayjs
  ) => {
    const url = `${BASE_URL}/tephigram/getTephigram`
    return request
      .get(url, {
        params: { bulletinID, modelID, date: date.toISOString() },
        responseType: 'blob'
      })
      .then((response) => {
        return response.data
      })
  },
  getBulletinScheduleSend: async (
    bulletinID: Bulletin['id']
  ): Promise<RequestBulletinMailConfigurationDTO[]> => {
    const url = `${BASE_URL}/getBulletinScheduleSend/${bulletinID}`
    return request.get(url).then((response) => {
      return response.data
    })
  },
  createBulletinScheduleSend: async (
    bulletinConfig: RequestBulletinMailConfigurationDTO
  ) => {
    const url = `${BASE_URL}/createBulletinScheduleSend/${bulletinConfig.ID_BULLETIN}`
    return request.post(url, bulletinConfig).then((response) => {
      return response.data
    })
  },
  updateBulletinScheduleSend: async (
    bulletinConfig: RequestBulletinMailConfigurationDTO
  ) => {
    const url = `${BASE_URL}/updateBulletinScheduleSend/${bulletinConfig.ID_BULLETIN_SCHEDULE_SEND}`
    return request.put(url, bulletinConfig).then((response) => {
      return response.data
    })
  },
  deleteBulletinScheduleSend: async (
    bulletinConfig: RequestBulletinMailConfigurationDTO
  ) => {
    const url = `${BASE_URL}/deleteBulletinScheduleSend/${bulletinConfig.ID_BULLETIN_SCHEDULE_SEND}`
    return request.delete(url).then((response) => {
      return response.data
    })
  },

  requestBulletinExtremadura: async (date: Dayjs, stationsID?: number[]) => {
    let url = `${BASE_URL}/data/getBulletinExtremaduraData`

    if (stationsID) {
      url += `?stationsID=[${stationsID.join(',')}]`
    }

    return request.post(url, { date: date.format('YYYY-MM-DD') })
  },

  downloadBulletinExtremadura: async (
    filename: string,
    type: string,
    extension: string
  ) => {
    try {
      const response = await request.get(
        `${BASE_URL}/data/downloadInformeExtremadura`,
        {
          responseType: 'blob'
        }
      )

      if (response.status === HttpStatusCode.Ok) {
        DownloadHelper.downloadFile(response.data, filename, type, extension)
      }
    } catch (error) {
      showToastError(t('error'), t('downloadFailedError'))
    }
  }
}
